/**
 * Responsible for creating an accordion based on elements existing in the DOM
 * @module modules/accordion
 */

import $ from 'jquery';

import registerJQueryPlugin from '../lib/register-jquery-plugin';

export const PLUGIN_NAME = 'accordion';
export const CLICK_EVENT = 'click';
export const AUTO_VALUE = 'auto';
export const BLOCK_VALUE = 'block';
export const CHECKED_ATTR = 'checked';
export const FORCE_OPEN_CLASS = '.accordion__item--force-open';
export const DEFAULTS = {
  accItem: '.accordion__item',
  accContent: '.accordion__content',
  accActive: 'accordion__item--active',
  accRadio: 'js-accordion-radio',
  accLabel: '.accordion__label',
  accSpeed: 500,
  accToggle: false,
  accDataToggleAttr: 'toggle',
  accQueryOpen: false,
  accDataQueryAttr: 'queryString',
  accScrollToView: false, // Default to true for backward compatibility
  accDataScrollAttr: 'scroll-to-view', // Data attribute name
  onComplete: () => {},
};

/**
 * Merges together all the options and the various conditions in which they
 * can be set.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} [options={}] - Any options provided upon initialization
 * @return {Object} Immutable version of the options
 */
const mergeOptions = ($el, options = {}) => {
  let opts = $.extend({}, DEFAULTS, options);
  const { accDataToggleAttr, accDataQueryAttr } = opts;

  if ($el.data(accDataToggleAttr) === true) {
    opts = $.extend({}, opts, { accToggle: true });
  }

  if ($el.data(accDataQueryAttr) === true) {
    opts = $.extend({}, opts, { accQueryOpen: true });
  }

  // Check for the scroll-to-view data attribute
  if ($el.data(opts.accDataScrollAttr) !== undefined) {
    opts = $.extend({}, opts, { accScrollToView: $el.data(opts.accDataScrollAttr) });
  }

  if ($el.data('acc-speed') !== undefined) {
    opts = $.extend({}, opts, { accSpeed: $el.data('acc-speed') });
  }

  return opts;
};




/**
 * Handles any sort of defaults when the accordion is called upon.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 */
const applyDefaultsToActive = ($el, { accActive, accContent, accLabel }) => {
  $(`.${accActive}`, $el)
    .find(accContent)
    .css({
      display: BLOCK_VALUE,
      height: AUTO_VALUE,
    })
    .find(accLabel)
    .attr('aria-expanded', 'true');
};

/**
 * Handles any sort of defaults when the accordion is using radio buttons.
 * @private
 * @param  {jQuery} $el - The container element
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 */
const applyDefaultsToRadio = ($el, { accActive, accContent, accItem, accRadio }) => {
  $(`.${accRadio}:${CHECKED_ATTR}`, $el)
    .parents(accItem)
    .addClass(accActive)
    .find(accContent)
    .css({ height: AUTO_VALUE });
};

/**
 * Closes the active tab
 * @private
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accItem - Selector representing the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */
const closeActiveTab = ($el, { accLabel, accActive, accContent, accItem, accSpeed }) => {
  $(`.${accActive}`, $el).find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function callback() {
      $(this).removeAttr('style');
    },
  );
  $el.find(accLabel).attr('aria-expanded', false);
  $el.find(accItem).removeClass(accActive);
};

/**
 * Opens a tab and provides any DOM manipulation and styles used to show a tab.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {jQuery} $parent - The parent of the interacted label
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {string} options.accRadio - Selector representing a radio button
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 * @param  {Function} options.onComplete - Called on when animation has
 * completed
 */
const openTab = ($el, $parent, { accLabel, accActive, accContent, accRadio, accSpeed,accScrollToView, onComplete }) => {
  const content = $parent.find(accContent);
  content.css({ height: AUTO_VALUE, display: BLOCK_VALUE });

  const contentHeight = content.outerHeight(true);

  content.css({ height: 0 }).stop().animate(
    { height: contentHeight },
    accSpeed,
    () => {
      content.css({ height: AUTO_VALUE });
      // Only scroll if data-scroll-to-view="true" is explicitly added to the element
      // First check if the parent accordion element has it
      const elScrollOption = $el.data('scroll-to-view');
      // Then check if this specific accordion item has it
      const parentScrollOption = $parent.data('scroll-to-view');
      
      // Only scroll if explicitly set to true (not just present)
      const shouldScroll = (elScrollOption === true || parentScrollOption === true);
      
      if (shouldScroll) {
        const headerOffset = $parent.offset().top;
        const fixedHeaderHeight = $('.site-header').outerHeight() || 0;
        const customOffset = parseInt($parent.data('scroll-offset') || $el.data('scroll-offset') || 0, 10);

        $('html, body').animate({
          scrollTop: headerOffset - fixedHeaderHeight - 30 - customOffset
        }, 300);
      }
      
      onComplete();
    },
  );
  $parent.find(accLabel).attr('aria-expanded', true);
  $parent
    .addClass(accActive)
    .children(accRadio).prop(CHECKED_ATTR, true);
};

/**
 * Closes an existing tab that has directly been interacted with.
 * @private
 * @param  {jQuery} $parent - The parent containing the entire tab.
 * @param  {Object} options - Any options provided upon initialization
 * @param  {string} options.accLabel - Selector representing the label className
 * @param  {string} options.accActive - Selector representing the active
 * className
 * @param  {string} options.accContent - Selector representing the content for
 * the tab
 * @param  {number} options.accSpeed - Speed in ms for the closing animation
 */
const closeExistingActiveTab = ($parent, { accLabel, accActive, accContent, accSpeed }) => {
  $parent.find(accContent).stop().animate(
    { height: 0 },
    accSpeed,
    function callback() {
      $(this).removeAttr('style');
      $parent.removeClass(accActive);
    },
  );
  $parent.find(accLabel).attr('aria-expanded', false);
};

/**
 * Handles the click event when a label has been iteracted with.
 * @private
 * @param {jQuery.Event} evt - A jQuery event
 * @param {HTMLElement} currentTarget - The current target interacted with
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} opts - Options passed along from initialization
 */
const itemClickHandler = ({ currentTarget }, $el, opts) => {
  const { accActive, accItem, accToggle } = opts;
  const $parent = $(currentTarget).closest(accItem);

  if (!$parent.hasClass(accActive)) {
    if (!accToggle) {
      closeActiveTab($el, opts);
    }

    openTab($el, $parent, opts);
  } else {
    closeExistingActiveTab($parent, opts);
  }
};



/**
 * Add aria-labelledby to modal content for accessible
 * audible title that corresponds with modal's heading
 * @param  {jQuery} $el
 */

const addAccContentAria = ($el) => {
  let $accLabel = $($el.find('.accordion__label'));
  if ($accLabel.length && !$accLabel.attr('id')) {
    const accContentTargetId = `accordion__content-${Math.ceil(Math.random() * 10000000000)}`;
    $accLabel.attr('id', accContentTargetId).attr('tabindex', '0');
  }
};



/**
 * Utility function to pull query parameter form URL
 * @private
 * @param {String} name - String to look for in URL
 */
const getParameterByName = (name) => {
    name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
    var regex = new RegExp("[\\?&]" + name + "=([^&#]*)"),
        results = regex.exec(location.search);
    return results === null ? "" : decodeURIComponent(results[1].replace(/\+/g, " "));
}

/**
 * Applies any sort of event handlers for the accordion.
 * @private
 * @param {jQuery} $el - Containing the elements for the accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
const applyEventHandlers = ($el, opts = {}) => {
   const { accLabel, accItem, accQueryOpen, accRadio } = opts;
  // check if accordion has data attr set to true
  if (accQueryOpen) {
    // check for query string in URL
    var accordionOpen = getParameterByName('accordionOpen');
  	if (accordionOpen) {
      // set parent for openTab
      const $parent = $el.find('#' + accordionOpen).closest(accItem);

      //  Set animation speed to 0 so default active tab closes as quick as possible.
      closeActiveTab($el, {...opts, accSpeed: 0});
      openTab($el, $parent, {...opts, accSpeed: 0});
    }
  }
  $el.on(CLICK_EVENT, `${opts.accItem}:not(${FORCE_OPEN_CLASS}) ${opts.accLabel}`, evt => itemClickHandler(evt, $el, opts));
  // Radio Accordion
  $el
    .find(`.${accRadio}`)
    .on(CLICK_EVENT, evt => {
      itemClickHandler(evt, $el, opts);
    });
};


/**
 * Initializes an accordion based on provided parameters.
 * @param {HTMLElement} el - An element containing the elements for the
 * accordion
 * @param {Object} options - Options used for wiring up the accordion
 * @see DEFAULTS
 */
export default function accordion(el, options = {}) {
  const $el = $(el);
  const opts = mergeOptions($el, options);
  addAccContentAria($el);
  applyDefaultsToActive($el, opts);
  applyDefaultsToRadio($el, opts);
  applyEventHandlers($el, opts);
}

// Expose the function as a jQuery plugin for ease of use
registerJQueryPlugin(PLUGIN_NAME, accordion);



// WEBPACK FOOTER //
// ./styleguide/src/assets/modules/accordion.js